import { endOfDay, setSeconds, startOfDay } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { GlobalFilterOptionsEnum } from "../enum/global-filter/GlobalFilterEnum";
import { IGlobalDateFilter } from "../models/filters/IFilters";
import { getAirportCode } from "../store/airport/AirportSelector";
import { useAppSelector } from "../store/StoreHooks";
import { getAirportConfig } from "../utils/common/CommonHelperFunctions";
import { getConvertedTime } from "../utils/common/DateHelperFunctions";
import {
  FlightFilterParams,
  FlightSelectionID,
  LayoutContext,
  LayoutContextType,
} from "./LayoutContext";
import { StaticGlobalFilterContext } from "./StaticGlobalFilterContext";

export type Timezone = "LT" | "UTC";

interface LayoutStateProviderProps {
  children: React.ReactNode;
}

/**
 * LayoutStateProvider manages all global layout state (timezone, currentTime, filters, etc.)
 * and makes it available via LayoutContext to all descendants (main content + sidebar).
 */
export const LayoutStateProvider: React.FC<LayoutStateProviderProps> = ({
  children,
}) => {
  const storedIsMapView = sessionStorage.getItem("isMapView")
    ? sessionStorage.getItem("isMapView") === "true"
    : false;
  const storedTimezone = sessionStorage.getItem("timezone")
    ? sessionStorage.getItem("timezone")
    : "LT";
  const storedIsMappedInView = sessionStorage.getItem("isMappedInView")
    ? sessionStorage.getItem("isMappedInView") === "true"
    : false;

  const [isMapView, setIsMapView] = useState<boolean>(storedIsMapView);
  const [isMappedInView, setIsMappedInView] =
    useState<boolean>(storedIsMappedInView);

  const [selectedFlight, setSelectedFlight] = useState<FlightSelectionID>({
    flightID: undefined,
    flightDocID: undefined,
  });
  const [flightFilterParams, setFlightFilterParams] =
    useState<FlightFilterParams>({
      legType: "",
      flightIDs: [],
    });

  const [timezone, setTimezone] = useState<Timezone>(
    storedTimezone as Timezone
  );

  const airportCode = useAppSelector((state) => {
    const stored = sessionStorage.getItem("airportCode");
    if (stored) {
      return stored;
    }
    return getAirportCode(state);
  });

  const airportConfig = getAirportConfig(airportCode);
  const { timeZoneId: airportTimeZone } = airportConfig;

  const [currentTime, setCurrentTime] = useState<Date>(
    setSeconds(getConvertedTime(new Date(), timezone, airportTimeZone), 0)
  );

  const startDate = startOfDay(new Date());
  const endDate = endOfDay(new Date());

  const [globalFilterDate, setGlobalFilterDate] = useState<IGlobalDateFilter>({
    startDate: startDate,
    endDate: endDate,
  });

  const [globalStaticFilterDate, setGlobalStaticFilterDate] =
    useState<IGlobalDateFilter>({
      startDate: startDate,
      endDate: endDate,
    });

  const [selectedFilter, setSelectedFilter] = useState<string>(
    GlobalFilterOptionsEnum.rolling
  );

  const urlPath = location?.pathname;
  const isFlight = urlPath?.startsWith("/flights");

  // Reset MappedIn view when navigating away from flights page
  useEffect(() => {
    if (!isFlight) {
      sessionStorage.setItem("isMappedInView", "false");
      setIsMappedInView(false);
    }
  }, [urlPath, isFlight]);

  // Persist values to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem("timezone", timezone ?? "LT");
    sessionStorage.setItem("isMapView", String(isMapView ?? false));
    sessionStorage.setItem("isMappedInView", String(isMappedInView ?? false));
  }, [timezone, isMapView, isMappedInView]);

  const contextValue: LayoutContextType = useMemo(
    () => ({
      isMapView,
      setIsMapView,
      isMappedInView,
      setIsMappedInView,
      selectedFlight,
      flightFilterParams,
      globalFilterDate,
      globalStaticFilterDate,
      timezone,
      currentTime,
      selectedFilter,
      setCurrentTime,
      setSelectedFilter,
      setSelectedFlight,
      setFlightFilterParams,
      setGlobalFilterDate,
      setGlobalStaticFilterDate,
      setTimezone,
    }),
    [
      isMapView,
      isMappedInView,
      selectedFlight,
      flightFilterParams,
      globalFilterDate,
      globalStaticFilterDate,
      timezone,
      currentTime,
      selectedFilter,
    ]
  );

  const staticGlobalFilterContextValue = useMemo(
    () => ({
      timezone,
      globalStaticFilterDate,
    }),
    [timezone, globalStaticFilterDate]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <StaticGlobalFilterContext.Provider
        value={staticGlobalFilterContextValue}
      >
        {children}
      </StaticGlobalFilterContext.Provider>
    </LayoutContext.Provider>
  );
};

export default LayoutStateProvider;
